import React, { useState, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GenericModal from '../../../components/modal/GenericModal';
import PageTable from '../../../components/pageComponents/PageTable';
import PageWrapper from '../../../components/pageComponents/PageWrapper';
import FormSelect from '../../../components/atoms/Select';
import TableSelectSearch from '../../../components/atoms/TableSelectSearch';
import useDidMountEffect from '../../../hooks/useDidMountEffect/useDidMountEffect';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import { requerimientoPersonalIndexForTPAPI } from '../../../consumers/backendApisUrls';

// ──────────────────────────────────────────────────────────────────────────
// Modal "Seleccionar Vacante" (28/08/2026) — se abre desde el campo "Vacante
// Destino" de NuevaConvocatoriaIndividual.jsx. Jody pidió que, en vez de un
// simple <select>, el usuario vea una grilla con más contexto para elegir la
// vacante correcta — la misma grilla que ya existe en "Gestión de
// Requerimientos" (ver pages/gestionRequerimientos/GestionRequerimientos.jsx
// + TableWebBody.jsx), sin las columnas "Fecha Completado" ni "tpo att /
// tpo abie" (a pedido explícito de Jody).
//
// Los nombres de columna/campo (IdRequerimientoPersonal, FechaRegistro,
// FechaAprobacion, Sede, Division, Gerencia, Area, Cargo,
// EspecialidadSeleccion, TipoEmpleado, AvanceRequerimiento, EstadoAprobacion,
// EstadoProceso, Colaborador) son deliberadamente los mismos que usa
// TableWebBody.jsx, para no tener que volver a mapear nada.
//
// Datos reales (28/08/2026): se conecta a `requerimientoPersonalIndexForTPAPI`
// (POST GestionRequerimiento/RequerimientoPersonalIndex) fijando los estados
// que califican una fila como "vacante" (RequerimientoPersonal
// IdEstadoAprobacion=14 "Aprobado" e IdEstadoProceso=23 "Abierto", ver
// modelo-datos-notas.md). Los encabezados con filtro (ícono de embudo)
// reutilizan los mismos componentes de esa pantalla real,
// `FormSelect`/`TableSelectSearch` (components/atoms/Select y
// TableSelectSearch) — misma elección de cuál usa cada columna que en
// GestionRequerimientos.jsx. El filtrado por columna se hace client-side
// sobre las filas cargadas; la paginación es del backend.
//
// Al hacer clic en el ícono de "Seleccionar esta vacante" de una fila, se
// llama a `onSeleccionar(fila)` (el padre guarda `fila.IdRequerimientoPersonal`
// como IdVacanteDestino) y se cierra este modal.
//
// 24/09/2026: además del prop `sede` (un solo Id, lo usa NuevaConvocatoriaIndividual.jsx
// con la Sede Destino elegida en el formulario), se agregó `sedes` (array de Ids,
// lo usa AsignarVacante.jsx con las hasta 3 sedes de interés del postulante —
// IdSedeInteres1/2/3 de GetDetalleForAsignacionTraslado) para prefiltrar por
// varias sedes a la vez. Ambos se combinan (sin duplicados) en IdsSedes; el
// backend (GetIndexRequerimientoPersonalForTP) ya soporta una lista de Sedes.
//
// 28/09/2026: se agregó `idCargo` (un solo Id, lo usa AsignarVacante.jsx solo
// cuando TP_Convocatoria.TipoProceso = 'T' — Traslado) para prefiltrar además
// por Cargo, comparando el Empleado.IdCargo del postulante contra
// RequerimientoPersonal.IdCargo. Se envía en IdsCargos (el backend ya filtra
// por esa lista — RequerimientoPersonalFilterViewModel.IdsCargos, mismo patrón
// que IdsSedes); en Promoción `idCargo` llega null y no se filtra por cargo.
// ──────────────────────────────────────────────────────────────────────────

// Filas esperadas ya en el estado de "vacante" (Aprobado + Abierto). Se fijan
// en el payload porque el endpoint de índice no filtra por defecto.
const ESTADO_APROBACION_VACANTE = 14;
const ESTADO_PROCESO_ABIERTO = 23;

// FormSelect/TableSelectSearch (componentes reales de Gestión de
// Requerimientos) esperan las opciones como {Value, Text} — distinto del
// {value, label} de react-select que usa el resto del módulo. Se arman a
// partir de los valores únicos de las filas cargadas.
const buildFilterOptions = (filas, campo) => {
    const valoresUnicos = [...new Set(filas.map(v => v[campo]).filter(Boolean))];
    return valoresUnicos.map(v => ({ Value: v, Text: v }));
}

const SeleccionarVacanteModal = ({ toggleModal, onSeleccionar, sede, sedes, idCargo }) => {
    const [selectedFilters, setSelectedFilters] = useState({});
    const [paginator, setPaginator] = useState({ CurrentPage: 1 });
    const [filas, setFilas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [managePostRequest] = useManagePostRequest();

    const manageSearch = async (page = 1) => {
        const api = requerimientoPersonalIndexForTPAPI();
        // `sede` (un solo Id) y `sedes` (array de Ids) se combinan sin duplicados.
        const idsSedes = [...new Set([
            ...(sede ? [Number(sede)] : []),
            ...(sedes ?? []).filter(Boolean).map(Number),
        ])];
        // `idCargo` (un solo Id, opcional) solo llega cuando el proceso es Traslado.
        const idsCargos = idCargo ? [Number(idCargo)] : [];
        const payload = {
            Page: page,
            IdsEstadoAprobacion: [ESTADO_APROBACION_VACANTE],
            IdsEstadoProceso: [ESTADO_PROCESO_ABIERTO],
            IdsSedes: idsSedes,
            IdsCargos: idsCargos,
        };
        await managePostRequest(api, payload, ({ data }) => {
            const { Items, TotalItems, FirsItemPage, LastItemPage, TotalPages, CurrentPage } = data;
            setFilas(Items || []);
            setPaginator({ TotalItems, FirsItemPage, LastItemPage, TotalPages, CurrentPage });
        }, false);
    };

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                await manageSearch(1);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Cuando PaginatorV2 cambia la página actual, vuelve a consultar el índice.
    useDidMountEffect(() => {
        manageSearch(paginator.CurrentPage);
    }, [paginator.CurrentPage]);

    const FILTROS = useMemo(() => ({
        Sede: buildFilterOptions(filas, "Sede"),
        Division: buildFilterOptions(filas, "Division"),
        Gerencia: buildFilterOptions(filas, "Gerencia"),
        Area: buildFilterOptions(filas, "Area"),
        Cargo: buildFilterOptions(filas, "Cargo"),
        TipoEmpleado: buildFilterOptions(filas, "TipoEmpleado"),
        EstadoAprobacion: buildFilterOptions(filas, "EstadoAprobacion"),
        EstadoProceso: buildFilterOptions(filas, "EstadoProceso"),
        Colaborador: buildFilterOptions(filas, "Colaborador"),
    }), [filas]);

    const handleFilterSelected = (res, campo) => {
        setSelectedFilters(lastData => ({ ...lastData, [campo]: res }));
    }

    const filasFiltradas = filas.filter(fila =>
        Object.entries(selectedFilters).every(([campo, seleccionados]) =>
            !seleccionados?.length || seleccionados.includes(fila[campo])
        )
    );

    const handleSeleccionar = (fila) => {
        onSeleccionar(fila);
        toggleModal();
    }

    const HEADERS = [
        { label: "Id" },
        { label: "Presupuesto" },
        { label: "Fecha de Registro" },
        { label: "Fecha de Aprobación" },
        {
            label: <DCenter><TableSelectSearch options={FILTROS.Sede} description="Sede"
                selectedOptions={selectedFilters.Sede ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "Sede")} /></DCenter>
        },
        {
            label: <DCenter><FormSelect options={FILTROS.Division} description="División"
                selectedOptions={selectedFilters.Division ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "Division")} /></DCenter>
        },
        {
            label: <DCenter><FormSelect options={FILTROS.Gerencia} description="Gerencia"
                selectedOptions={selectedFilters.Gerencia ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "Gerencia")} /></DCenter>
        },
        {
            label: <DCenter><TableSelectSearch options={FILTROS.Area} description="Área"
                selectedOptions={selectedFilters.Area ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "Area")} /></DCenter>
        },
        {
            label: <DCenter><TableSelectSearch options={FILTROS.Cargo} description="Cargo"
                selectedOptions={selectedFilters.Cargo ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "Cargo")} /></DCenter>
        },
        { label: "Especialidad" },
        {
            label: <DCenter><TableSelectSearch options={FILTROS.TipoEmpleado} description="Jornada Laboral"
                selectedOptions={selectedFilters.TipoEmpleado ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "TipoEmpleado")} /></DCenter>
        },
        { label: "Avance" },
        {
            label: <DCenter><FormSelect options={FILTROS.EstadoAprobacion} description="Est. Aprob."
                selectedOptions={selectedFilters.EstadoAprobacion ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "EstadoAprobacion")} /></DCenter>
        },
        {
            label: <DCenter><FormSelect options={FILTROS.EstadoProceso} description="Est. Proceso"
                selectedOptions={selectedFilters.EstadoProceso ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "EstadoProceso")} /></DCenter>
        },
        {
            label: <DCenter><TableSelectSearch options={FILTROS.Colaborador} description="Responsable"
                selectedOptions={selectedFilters.Colaborador ?? []} setSelectedOptions={(res) => handleFilterSelected(res, "Colaborador")} /></DCenter>
        },
        { label: "Acciones" },
    ];

    return (
        <GenericModal showModal={toggleModal} titulo="Seleccionar Vacante" width="1250px">
            <div className="modal-body">
                <PageWrapper isLoading={isLoading}>
                    <PageTable headers={HEADERS} paginator={paginator} setPaginator={setPaginator}>
                        <tbody className="listar-tbody">
                            {filasFiltradas.length === 0 &&
                                <tr>
                                    <td colSpan={HEADERS.length} className="text-center text-muted">No se encontraron vacantes con esos filtros.</td>
                                </tr>
                            }
                            {filasFiltradas.map((fila) => (
                                <tr key={fila.IdRequerimientoPersonal}>
                                    <td className="text-center">{fila.IdRequerimientoPersonal}</td>
                                    <td className="text-center">{fila.NombrePresupuesto}</td>
                                    <td className="text-center">{fila.FechaRegistro}</td>
                                    <td className="text-center">{fila.FechaAprobacion}</td>
                                    <td>{fila.Sede}</td>
                                    <td>{fila.Division}</td>
                                    <td>{fila.Gerencia}</td>
                                    <td>{fila.Area}</td>
                                    <td className="text-center">{fila.Cargo}</td>
                                    <td className="text-center">{fila.EspecialidadSeleccion}</td>
                                    <td className="text-center">{fila.TipoEmpleado}</td>
                                    <td className="text-center">{fila.AvanceRequerimiento}</td>
                                    <td className="text-center">{fila.EstadoAprobacion}</td>
                                    <td className="text-center">{fila.EstadoProceso}</td>
                                    <td>{fila.Colaborador}</td>
                                    <td className="text-center">
                                        <FontAwesomeIcon icon="check-circle" size="lg"
                                            className="hand-pointer"
                                            title="Seleccionar esta vacante"
                                            onClick={() => handleSeleccionar(fila)} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </PageTable>
                </PageWrapper>
            </div>
        </GenericModal>
    )
}

export default SeleccionarVacanteModal

const DCenter = ({ children }) => (
    <div className="d-flex justify-content-center">{children}</div>
)
